import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, AlertCircle, AlertTriangle, CheckCircle, Info, WifiOff } from 'lucide-react';
import { useCustomerApi } from '@/contexts/DashboardCustomerContext';
import { SkeletonLoader } from '@/components/common/SkeletonLoader';

interface AutoSupportSummary {
  total_systems: number;
  enabled: number;
  disabled: number;
  recently_stopped: number;
  stale_asups: number;
  no_receive_date: number;
  transport_breakdown: Record<string, number>;
}

interface AutoSupportResponse {
  status: string;
  summary: AutoSupportSummary;
  issues: {
    stale_systems: Array<{
      system_id: string;
      host_name: string;
      days_old: number;
      last_received: string;
    }>;
    no_receive_date: Array<{
      system_id: string;
      host_name: string;
      generated_date: string | null;
    }>;
  };
}

interface AutoSupportWidgetProps {
  refreshTrigger?: number;
}

export const AutoSupportWidget: React.FC<AutoSupportWidgetProps> = ({ refreshTrigger }) => {
  const apiClient = useCustomerApi();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AutoSupportResponse | null>(null);

  useEffect(() => {
    fetchData();
  }, [refreshTrigger]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<AutoSupportResponse>('/dashboard/autosupport');
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch AutoSupport data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <SkeletonLoader variant="chart" />;
  }

  if (!data || data.status === 'no_config') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-gray-100 dark:bg-slate-700/50">
            <Activity className="w-6 h-6 text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            AutoSupport Status
          </h3>
        </div>
        <div className="text-center py-8">
          <Info className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-600 dark:text-gray-400">
            No NetApp configuration found
          </p>
        </div>
      </motion.div>
    );
  }

  const summary = data.summary;
  const issues = data.issues;
  const hasIssues = summary.disabled > 0 || summary.recently_stopped > 0 ||
                    summary.stale_asups > 0 || summary.no_receive_date > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${
            hasIssues
              ? 'bg-orange-100 dark:bg-orange-900/30'
              : 'bg-green-100 dark:bg-green-900/30'
          }`}>
            <Activity className={`w-6 h-6 ${
              hasIssues
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-green-600 dark:text-green-400'
            }`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              AutoSupport Status
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {summary.total_systems} total systems
            </p>
          </div>
        </div>
        {!hasIssues && (
          <CheckCircle className="w-6 h-6 text-green-500" />
        )}
      </div>

      {/* Summary Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Enabled */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-xs font-medium text-green-700 dark:text-green-300">
              Enabled
            </span>
          </div>
          <div className="text-2xl font-bold text-green-900 dark:text-green-100">
            {summary.enabled}
          </div>
          <div className="text-xs text-green-600 dark:text-green-400 mt-1">
            {summary.total_systems > 0
              ? `${Math.round((summary.enabled / summary.total_systems) * 100)}%`
              : '0%'
            }
          </div>
        </div>

        {/* Disabled */}
        {summary.disabled > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <WifiOff className="w-4 h-4 text-red-600 dark:text-red-400" />
              <span className="text-xs font-medium text-red-700 dark:text-red-300">
                Disabled
              </span>
            </div>
            <div className="text-2xl font-bold text-red-900 dark:text-red-100">
              {summary.disabled}
            </div>
            <div className="text-xs text-red-600 dark:text-red-400 mt-1">
              Action required
            </div>
          </div>
        )}

        {/* Recently Stopped */}
        {summary.recently_stopped > 0 && (
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                Stopped
              </span>
            </div>
            <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">
              {summary.recently_stopped}
            </div>
            <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
              Recently stopped sending
            </div>
          </div>
        )}

        {/* Stale ASUPs */}
        {summary.stale_asups > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
              <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300">
                Stale
              </span>
            </div>
            <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
              {summary.stale_asups}
            </div>
            <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
              &gt;3 days old
            </div>
          </div>
        )}

        {/* No Receive Date */}
        {summary.no_receive_date > 0 && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Info className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                No Receive Date
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {summary.no_receive_date}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Not received by NetApp
            </div>
          </div>
        )}
      </div>

      {/* Issues Details */}
      {hasIssues && (
        <div className="space-y-3">
          {issues.stale_systems.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/10 rounded-lg p-3 border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                    {issues.stale_systems.length} system{issues.stale_systems.length !== 1 ? 's' : ''} with stale AutoSupport
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    Last ASUP received more than 3 days ago
                  </p>
                  <div className="mt-2 space-y-1">
                    {issues.stale_systems.slice(0, 3).map((sys) => (
                      <div key={sys.system_id} className="text-xs text-yellow-800 dark:text-yellow-200">
                        • {sys.host_name}: {sys.days_old} days old
                      </div>
                    ))}
                    {issues.stale_systems.length > 3 && (
                      <div className="text-xs text-yellow-700 dark:text-yellow-300 italic">
                        +{issues.stale_systems.length - 3} more...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {summary.disabled > 0 && (
            <div className="bg-red-50 dark:bg-red-900/10 rounded-lg p-3 border border-red-200 dark:border-red-800">
              <div className="flex items-start gap-2">
                <WifiOff className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-900 dark:text-red-100">
                    {summary.disabled} system{summary.disabled !== 1 ? 's' : ''} with AutoSupport disabled
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                    Enable AutoSupport to receive health notifications
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transport Breakdown */}
      {Object.keys(summary.transport_breakdown).length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Transport Methods
          </h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(summary.transport_breakdown).map(([transport, count]) => (
              <div
                key={transport}
                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                {transport}: {count}
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default AutoSupportWidget;
